import React, { useState } from "react";
import {
  Button,
  ButtonGroup,
  Form,
  InputGroup,
  Card,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const presetAmounts = [10, 25, 50, 100];

export default function DonationForm({
  setDonationAmount,
  donationAmount,
  setDedicatedTo,
  onProceedToContact,
}) {
  const [isDedicated, setIsDedicated] = useState(true);
  const [includeName, setIncludeName] = useState(false);
  const [dedicationName, setDedicationName] = useState("");
  const [touched, setTouched] = useState(false);

  // local string version for the input
  const [amountInput, setAmountInput] = useState(
    donationAmount != null ? String(donationAmount) : ""
  );

  const isAmountValid =
    typeof donationAmount === "number" &&
    !Number.isNaN(donationAmount) &&
    donationAmount >= 5;

  const isDedicationValid =
    !isDedicated || (includeName ? dedicationName.trim().length > 0 : true);

  const canDonate = isAmountValid && isDedicationValid;

  const handleAmountChange = (e) => {
    const v = e.target.value;

    // allow empty
    if (v === "") {
      setAmountInput("");
      setDonationAmount(null);
      setTouched(true);
      return;
    }

    // allow only numbers w/ max 2 decimals
    const decimalRegex = /^\d*(\.\d{0,2})?$/;
    if (!decimalRegex.test(v)) {
      return;
    }

    setAmountInput(v);
    setTouched(true);

    // try to convert to number
    const num = Number(v);
    if (Number.isNaN(num)) {
      setDonationAmount(null);
    } else {
      setDonationAmount(num);
    }
  };

  // when user clicks a preset, we should also update the input string
  const handlePresetClick = (value) => {
    setDonationAmount(value);
    setAmountInput(String(value));
    setTouched(true);
  };

  return (
    <Card style={{ maxWidth: "400px", padding: "1.5rem", margin: "auto" }}>
      <h4 className="mb-3 text-center">Choose your donation</h4>

      <ButtonGroup className="mb-3 d-flex flex-wrap gap-2 pb-3">
        {presetAmounts.map((value) => (
          <Button
            key={value}
            variant={donationAmount === value ? "primary" : "outline-secondary"}
            onClick={() => handlePresetClick(value)}
            className="flex-fill"
          >
            ${value}
          </Button>
        ))}
      </ButtonGroup>

      <Form.Group className="mb-3 pb-2">
        <InputGroup>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Enter amount"
            value={amountInput}
            onChange={handleAmountChange}
            isInvalid={touched && !isAmountValid}
            inputMode="decimal"
          />
          <InputGroup.Text>USD</InputGroup.Text>
        </InputGroup>

        {touched && !isAmountValid && (
          <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>
            Must enter an amount of $5.00 or more.
          </div>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <div className="d-flex align-items-center gap-2">
          <Form.Check
            type="checkbox"
            checked={isDedicated}
            onChange={() => {
              setIsDedicated(!isDedicated);
              setTouched(true);
              if (isDedicated) {
                setIncludeName(false);
                setDedicationName("");
              }
            }}
          />
          <Form.Label className="mb-0">Dedicate this donation</Form.Label>
        </div>

        {isDedicated && (
          <>
            <Form.Control
              type="text"
              placeholder="Name of someone special to you"
              className="p-2 mb-2"
              value={dedicationName}
              onChange={(e) => {
                setDedicationName(e.target.value);
                setDedicatedTo && setDedicatedTo(e.target.value);
              }}
              isInvalid={
                touched && includeName && dedicationName.trim().length === 0
              }
            />
            <Form.Control.Feedback type="invalid">
              Please enter a name to include with the donation.
            </Form.Control.Feedback>
          </>
        )}
      </Form.Group>

      <Button
        variant="warning"
        size="lg"
        className="w-100 fw-bold text-dark"
        disabled={!canDonate}
        onClick={() => {
          if (!canDonate) return;
          onProceedToContact && onProceedToContact();
        }}
      >
        DONATE
      </Button>
    </Card>
  );
}
